/**
 * Which performance, if any, is waiting to be played.
 *
 * Same split as the tour and the first meeting: "is one running right now" is
 * a property of this page load and lives in memory, "has this account already
 * had it" is a property of the account and lives in the awards table (see
 * `glitime/moments.ts`).
 *
 * At most one is ever pending. Two milestones landing on the same commit — a
 * thousandth entry that is also a hundredth straight day — is rare enough that
 * queueing two full-screen performances back to back would be worse than
 * letting the second one wait for the next app open, which it will do by
 * itself: the detector re-runs on every launch until the moment is recorded.
 */

import { create } from 'zustand'
import type { MomentKind } from '../glitime/moments.js'
import type { CeremonyCopy } from './copy.js'

export interface PendingCeremony {
  /** What to record once it has actually been played through. */
  kind: MomentKind
  id: string
  copy: CeremonyCopy
}

interface CeremonyState {
  pending: PendingCeremony | null
}

export const useCeremony = create<CeremonyState>(() => ({ pending: null }))

/** Offer one. Ignored if something is already waiting. */
export function offerCeremony(next: PendingCeremony): void {
  if (useCeremony.getState().pending != null) return
  useCeremony.setState({ pending: next })
}

export function clearCeremony(): void {
  useCeremony.setState({ pending: null })
}
